import { useState, useEffect, useCallback } from "react";
import { Tag, ChevronDown, Copy, Download, Check, Sliders } from "lucide-react";
import QRCode from "qrcode";

function buildUrl(base, source, medium, campaign) {
  if (!base) return "";
  try {
    const url = new URL(base.startsWith("http") ? base : `https://${base}`);
    if (source) url.searchParams.set("utm_source", source);
    if (medium) url.searchParams.set("utm_medium", medium);
    if (campaign) url.searchParams.set("utm_campaign", campaign);
    return url.toString();
  } catch {
    return base;
  }
}

const QR_OPTS = {
  width: 1024,
  margin: 2,
  errorCorrectionLevel: "H",
  color: { dark: "#000000", light: "#ffffff" },
};

function Field({ label, optional, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-500">
        {label}{optional && <span className="ml-1 text-zinc-700">optional</span>}
      </label>
      <input
        {...props}
        className="w-full border-b border-zinc-800 bg-transparent py-2 text-sm text-zinc-100 placeholder-zinc-700 outline-none transition-colors focus:border-zinc-500"
      />
    </div>
  );
}

export default function QrPage() {
  const [baseUrl, setBaseUrl] = useState("https://tmtt.link");
  const [utmSource, setUtmSource] = useState("qr");
  const [utmMedium, setUtmMedium] = useState("sticker_en");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmOpen, setUtmOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");

  const finalUrl = buildUrl(baseUrl, utmSource, utmMedium, utmCampaign);

  const renderQr = useCallback(async () => {
    if (!finalUrl) { setQrDataUrl(""); return; }
    try {
      setQrDataUrl(await QRCode.toDataURL(finalUrl, QR_OPTS));
    } catch {
      setQrDataUrl("");
    }
  }, [finalUrl]);

  useEffect(() => { renderQr(); }, [renderQr]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(finalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = "qr.png";
    a.click();
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <header className="border-b border-zinc-900">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-5">
          <a href="/" className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-zinc-950">
              <Tag className="h-3.5 w-3.5" />
            </span>
            <span className="text-sm font-semibold tracking-tight">TMTT.Link</span>
          </a>
          <span className="text-xs text-zinc-600">QR Generator</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-100">QR Code Generator</h1>
          <p className="mt-1 text-sm text-zinc-600">URL을 입력하고 QR 코드를 다운로드하세요.</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_220px] lg:items-start">
          {/* Form */}
          <div className="space-y-8">
            {/* URL */}
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-zinc-600">URL</p>
              <Field
                type="url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://tmtt.link"
              />
            </div>

            {/* UTM */}
            <div>
              <button
                onClick={() => setUtmOpen((v) => !v)}
                className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                <Sliders className="h-3 w-3" />
                UTM Parameters
                <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${utmOpen ? "rotate-180" : ""}`} />
              </button>

              {utmOpen && (
                <div className="grid gap-5 sm:grid-cols-3">
                  <Field
                    label="utm_source"
                    type="text"
                    value={utmSource}
                    onChange={(e) => setUtmSource(e.target.value)}
                    placeholder="qr"
                  />
                  <Field
                    label="utm_medium"
                    type="text"
                    value={utmMedium}
                    onChange={(e) => setUtmMedium(e.target.value)}
                    placeholder="sticker_en"
                  />
                  <Field
                    label="utm_campaign"
                    optional
                    type="text"
                    value={utmCampaign}
                    onChange={(e) => setUtmCampaign(e.target.value)}
                    placeholder="—"
                  />
                </div>
              )}
            </div>

            {/* Final URL */}
            <div className="border-l-2 border-zinc-800 pl-4">
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 break-all font-mono text-xs leading-relaxed text-zinc-500">
                  {finalUrl || "—"}
                </p>
                <button
                  onClick={handleCopy}
                  disabled={!finalUrl}
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-300 transition-colors disabled:opacity-30"
                >
                  {copied
                    ? <><Check className="h-3 w-3 text-green-500" /><span className="text-green-500">copied</span></>
                    : <><Copy className="h-3 w-3" />copy</>
                  }
                </button>
              </div>
            </div>
          </div>

          {/* QR Preview */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white p-3 inline-block">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR code" className="block w-[172px] h-[172px]" />
              ) : (
                <div className="w-[172px] h-[172px] flex items-center justify-center bg-zinc-100 text-zinc-400 text-xs">
                  URL 입력 필요
                </div>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleDownload}
                disabled={!qrDataUrl}
                className="flex items-center gap-1.5 border border-zinc-800 px-3 py-1.5 text-xs text-zinc-400 hover:border-zinc-600 hover:text-zinc-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Download className="h-3 w-3" />
                PNG
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
